import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const sessionExpired = searchParams.get('session') === 'expired';

  useEffect(() => {
    if (sessionExpired) {
      toast.error('Session expired. Please log in again.');
    }
  }, [sessionExpired]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      // If the user arrived via "Notify Me at Launch" (?notify=1) auto-opt them in.
      if (searchParams.get('notify') === '1') {
        try {
          await axios.post(
            `${BACKEND_URL}/api/notify-me`,
            { source: 'login_post_launch_cta' },
            { withCredentials: true },
          );
          toast.success("You're on the list — we'll notify you when bookings open.");
        } catch {
          toast.success('Login successful!');
        }
      } else {
        toast.success('Login successful!');
      }
      // Honour ?next=… (used by invite flow). Fall back to a pending invite
      // token saved in sessionStorage by the AcceptInvitePage, then "/".
      let next = searchParams.get('next');
      if (!next) {
        try {
          const pending = sessionStorage.getItem('vp_pending_invite_token');
          if (pending) next = `/team/accept/${pending}`;
        } catch {}
      }
      // Allow only same-origin relative paths to prevent open-redirect.
      if (next && next.startsWith('/') && !next.startsWith('//')) {
        navigate(next);
      } else {
        navigate('/');
      }
    } else {
      toast.error(result.error || 'Login failed');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#E5E9E6]">
          <div className="text-center mb-8">
            <h1
              className="text-4xl font-bold text-[#112318] mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="login-title"
            >
              Welcome Back
            </h1>
            <p className="text-[#4A5D50]">Sign in to your VinePass account</p>
          </div>

          {sessionExpired && (
            <div
              className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              data-testid="session-expired-banner"
              role="status"
            >
              <strong className="block font-medium">Session expired</strong>
              Please log in again to continue. We&rsquo;ll bring you straight back to where you were.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#4A5D50]">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#4A5D50]">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1"
                data-testid="login-password-input"
              />
              <div className="mt-1.5 text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-[#4A5D50] hover:text-[#1F3D2B] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 font-medium transition-all shadow-md text-lg"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#4A5D50]">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#1F3D2B] font-semibold hover:underline" data-testid="register-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;